import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { UUIDsEqual } from '@memberjunction/global';
import {
  mjBizAppsIssuesIssueEntity,
  mjBizAppsIssuesIssueStatusEntity,
  mjBizAppsIssuesIssueTypeEntity,
  mjBizAppsIssuesIssueCommentEntity,
} from '@mj-biz-apps/issues-entities';
import { mjBizAppsCommonPersonEntity, ResolvePersonIDForUser } from '@mj-biz-apps/common-entities';
import { MJRecordChangeEntity } from '@memberjunction/core-entities';
import { DownloadBase64File } from '../../../utils/download';
import { MessageEditorComponent } from '../../message-editor/message-editor.component';
import { BuildTicketTimeline, ChannelMessageEntityExtendedClient, FormatSentRecipients, IzzyChannelMessageNoteEntity, IzzyMetadataEngine, IzzyTicketSLAStateEntity, IzzyTicketSuggestionEntity, IzzyMacroEntity, TicketState, TicketTimelineEntry, TimelineAuthoredLike } from 'mj_generatedentities';

/**
 * A single chronological entry in the unified cross-channel timeline (PRD v4.0 §11.4).
 * Phase 1 surfaces the two message-derived kinds; comments/activity/tasks land in later phases.
 */
/** A candidate survivor ticket shown in the merge picker. */
interface MergeCandidate {
  id: string;
  issueNumber: string;
  title: string;
  reporterPersonId: string | null;
}

/**
 * Timeline entry shape — the shared TicketTimeline definition (GeneratedEntities), also
 * consumed by the Export Ticket PDF action so exports cannot drift from this screen.
 */
type TimelineEntry = TicketTimelineEntry;

/**
 * One event on the Timeline tab — the ticket's *lifecycle*, which the Conversation pane does not
 * show: status/priority/assignee transitions (from __mj.RecordChange), SLA clock events, and a
 * single compact line per message rather than its body (no duplication of the Conversation pane).
 */
interface ActivityEntry {
  when: Date;
  /** Font Awesome class for the rail icon. */
  icon: string;
  /** Primary line, e.g. "Status changed  New → Open". */
  label: string;
  /** Optional second line — who did it, or extra context. */
  detail?: string;
  /** Coarse kind, drives the rail colour. */
  kind: 'created' | 'field' | 'sla' | 'message' | 'comment' | 'closed' | 'escalated';
}

/**
 * Ticket Detail — the unified cross-channel timeline (PRD v4.0 §11.3), the signature Izzy view.
 *
 * Phase 1 scope: interleave the ChannelMessages linked to the ticket (via ChannelMessage.IssueID)
 * across all channels into one chronological timeline, alongside a read-only properties panel.
 * Later phases layer in IssueComments, activity/SLA events, linked Tasks, the reply composer,
 * macros, editable properties, assignment and collision detection (§11.3–11.6, §12–14).
 */
@Component({
  standalone: false,
  selector: 'izzy-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.css'],
})
export class TicketDetailComponent implements OnInit, OnChanges, OnDestroy {
  /** The Issue (ticket) to display. */
  @Input() issueId: string | undefined;
  /** Emitted when the agent navigates back to the queue. */
  @Output() back = new EventEmitter<void>();

  protected issue: mjBizAppsIssuesIssueEntity | null = null;
  /** Real per-org ticket number ({PREFIX}-####, PRD §5.2); null until backfilled / for legacy tickets. */
  protected ticketNumber: string | null = null;
  protected status: mjBizAppsIssuesIssueStatusEntity | null = null;
  protected type: mjBizAppsIssuesIssueTypeEntity | null = null;
  /** All statuses (ordered) for the status-change dropdown. */
  protected statuses: mjBizAppsIssuesIssueStatusEntity[] = [];
  protected isChangingStatus = false;
  protected timeline: TimelineEntry[] = [];
  /** The ChannelMessage entities backing the timeline — kept so a reply can be approved/rejected in-place. */
  private messageEntities: ChannelMessageEntityExtendedClient[] = [];
  /** The ticket's comments/notes — kept so the Timeline tab can show when each was added. */
  private issueComments: mjBizAppsIssuesIssueCommentEntity[] = [];
  /** Staff notes written on this ticket's individual messages (message-level, flow in one way). */
  private messageNotes: IzzyChannelMessageNoteEntity[] = [];
  /** Message ID currently being approved/rejected (drives per-reply button spinner/disable). */
  protected approvingMessageId: string | null = null;
  /** Message ID whose reply is being edited inline (null = not editing). */
  protected editingMessageId: string | null = null;
  /** Channel + inbound text of the reply being edited inline (AI assistant scope). */
  protected editingChannelId: string | null = null;
  protected editingThreadContext: string | null = null;
  /** Working copy of the reply text while editing (seeds the editor's currentContent). */
  protected editedReplyContent: string = '';
  /** The AI's original generated reply, for the editor's track-changes/diff baseline. */
  protected editingOriginalContent: string = '';
  /** Resolved assignee display name (Phase 2), or null when unassigned. */
  protected assigneeName: string | null = null;
  // --- Manual assignment (any staff member, no role gate) ---
  /** Org people assignable from the dropdown — excludes the acting user (who uses "Assign to me"). */
  protected assignablePeople: { id: string; name: string }[] = [];
  /** The acting user's linked Person ID (for "Assign to me"); null if the user has no linked Person. */
  protected actingPid: string | null = null;
  protected isAssigning = false;

  // --- Collision detection (§11.5): live "who else is viewing this ticket" presence ---
  /** Other agents currently viewing this ticket, keyed by PersonID → { name, lastSeen(ms) }. */
  private presenceMap = new Map<string, { name: string; lastSeen: number }>();
  /** Display names of other current viewers, for the banner. */
  protected otherViewers: string[] = [];
  private presenceSub?: { unsubscribe(): void };
  private presenceHeartbeat?: ReturnType<typeof setInterval>;
  private presenceSweep?: ReturnType<typeof setInterval>;
  private presenceIssueId: string | null = null;  // the ticket we announced presence on
  /** Snapshot of the ticket's UpdatedAt when loaded — for the stale-write guard. */
  private loadedUpdatedAt: string | null = null;
  /** The ticket's SLA state (Phase 3), or null when it has no SLA clock. */
  protected sla: IzzyTicketSLAStateEntity | null = null;

  // --- Right-pane tabs: everything an agent needs without leaving the ticket -------------------
  /** Which right-pane tab is showing. */
  protected activeTab: 'properties' | 'timeline' | 'analysis' = 'properties';
  /** The ticket the tab state belongs to, so an in-place refresh doesn't reset the agent's tab. */
  private loadedIssueId: string | null = null;

  // --- Timeline tab (ticket lifecycle audit) ---
  protected activity: ActivityEntry[] = [];
  protected isActivityLoading = false;
  protected activityError = '';
  /** Person display names referenced by the change history, keyed by PersonID. */
  private activityPersonNames = new Map<string, string>();
  /** True once the audit history has been fetched, so opening the tab again doesn't refetch. */
  private activityLoaded = false;

  // --- AI Analysis tab -------------------------------------------------------------------------
  /**
   * Messages on this ticket that actually carry AI analysis. An Issue has no AIConfidenceScore of
   * its own — the score, rubric reasoning and ChannelID all live on ChannelMessage — so the tab
   * analyses one selected message at a time.
   */
  protected analysisMessages: ChannelMessageEntityExtendedClient[] = [];
  /** Which message the analysis pane is showing. */
  protected selectedAnalysisMessageId: string | null = null;
  /** The ticket's owning org, resolved from a linked channel — for the pane's credential deep-link. */
  protected organizationId: string | null = null;
  /** True while a re-score is in flight (swaps the pane for the evaluating placeholder). */
  protected isRescoring = false;
  /** True while the re-score request is being submitted (disables the button). */
  protected isReprocessing = false;

  protected isLoading = true;
  protected errorMessage = '';

  // --- Escalation (§10.6) --------------------------------------------------
  /** Whether the escalate panel (reason + confirm) is open. */
  protected showEscalatePanel = false;
  /** Optional reason the agent gives for escalating — lands in the ticket's internal note. */
  protected escalateReason = '';
  protected isEscalating = false;
  protected escalateError = '';

  // --- Export PDF (plans/prd-pdf-export.md) --------------------------------
  /** Whether the export panel (internal-notes checkbox + confirm) is open. */
  protected showExportPanel = false;
  /** Include staff-only internal notes in the PDF. Defaults OFF — exports often leave the org. */
  protected exportIncludeInternalNotes = false;
  protected isExporting = false;
  protected exportError = '';

  // --- Merge (§7.4) -------------------------------------------------------
  /** Whether the merge panel is open. */
  protected showMergePanel = false;
  /** Free-text search for the survivor ticket. */
  protected mergeSearch = '';
  /** Candidate survivor tickets (other open tickets) matching the search. */
  protected mergeCandidates: MergeCandidate[] = [];
  /** The chosen survivor this ticket will be merged INTO. */
  protected selectedSurvivor: MergeCandidate | null = null;
  protected isMerging = false;
  protected mergeError = '';
  /** After a ticket merge, if reporters differ, we prompt to also merge the People. */
  protected showReporterMergePrompt = false;
  /** Resolved info for the reporter-merge prompt. */
  protected reporterMerge: { survivorPersonId: string; loserPersonId: string; survivorName: string; loserName: string } | null = null;

  // --- Follow-up suggestion (§6.4 "suggest, don't auto-link") -------------
  /** An Open suggestion where THIS ticket may be a follow-up to another (the merge target). */
  protected suggestion: IzzyTicketSuggestionEntity | null = null;
  /** Display label + reason for the suggested ticket, resolved for the banner. */
  protected suggestionInfo: { issueNumber: string; reason: string } | null = null;
  protected isResolvingSuggestion = false;

  // --- Comments / notes composer (mirrors MJ conversations: parent persists, composer is dumb) ---
  /** Resolved author display names, keyed by IssueComment ID. */
  private commentAuthorNames = new Map<string, string>();
  /** Composer text. */
  protected commentBody = '';
  /** Outbound reply body (HTML from the rich editor); internal notes use commentBody (plain text). */
  protected replyBody = '';
  /**
   * Initial content for the reply editor. Never updated per keystroke (that would round-trip the
   * live body back into Quill and jump the caret); set only when the editor is recreated after a
   * tab switch, so a draft survives Internal note ↔ Reply.
   */
  protected replyBodySeed = '';
  /** Channel of the ticket's latest inbound (the reply goes out on it; scopes the AI assistant). */
  protected composerChannelId: string | null = null;
  /** The latest inbound message text, handed to the assistant's "Help me write". */
  protected composerThreadContext: string | null = null;
  @ViewChild('replyEditor') replyEditor?: MessageEditorComponent;
  /** Which kind of comment the composer will write: internal note vs customer-facing reply. */
  protected commentSource: 'internal' | 'outbound' = 'internal';
  protected isAddingComment = false;

  // ========== Outbound reply recipients (CC / reply-all) ==========
  /** Per-message Reply vs Reply All choice; ReplyAllDefault picks the initial position. */
  protected replyAll = false;
  /** Editable comma-delimited CC list passed to the Send Ticket Reply action. */
  protected ccInput = '';
  /** True when the ticket's latest inbound email had recipients beyond the sender. */
  protected replyAllAvailable = false;
  private replyAllPrefill: string[] = [];
  protected commentError = '';

  // --- Macros (§11.6): canned replies inserted into the composer -------------------------------
  /** Org + own-Personal + own-Team macros available to the acting agent. */
  protected macros: IzzyMacroEntity[] = [];
  protected showMacroMenu = false;
  protected macroSearch = '';
  /** Cached values for {{placeholder}} resolution. */
  private actingAgentFirstName = '';
  private requesterFirstName = '';
  private orgName = '';

  // --- Resizable properties pane (mirrors message-editor's drag splitter convention) ----------
  @ViewChild('detailBody') private detailBody?: ElementRef<HTMLElement>;
  /** Properties pane width as a % of the body; the timeline takes the rest. Clamped 20–60%. */
  protected propsWidthPct = 26;
  protected isDraggingSplitter = false;

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['issueId'] && !changes['issueId'].firstChange) {
      this.stopPresence();  // leave the previous ticket before loading the new one
      void this.load();
    }
  }

  ngOnDestroy(): void {
    this.stopPresence();
  }

  /**
   * Org-facing label. Prefers the real per-org TicketNumber ({PREFIX}-####, PRD §5.2); falls back to
   * the legacy faked label derived from the per-AppScope IssueNumber for tickets created before per-org
   * numbering (no IssueTicketNumber row yet).
   */
  get ticketLabel(): string {
    if (this.ticketNumber) return this.ticketNumber;
    const num = this.issue?.IssueNumber || '';
    const seq = num.includes('-') ? num.split('-').pop() : num;
    return seq ? `TKT-${seq.padStart(4, '0')}` : 'TKT-?';
  }

  private async load(): Promise<void> {
    if (!this.issueId) {
      this.issue = null;
      this.timeline = [];
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await IzzyMetadataEngine.Instance.Config(false);
      const rv = new RunView();

      const [issueRes, statusRes, typeRes, msgRes, commentRes, ticketNumRes] = await rv.RunViews([
        {
          EntityName: 'MJ_BizApps_Issues: Issues',
          ExtraFilter: `ID = '${this.issueId}'`,
          ResultType: 'entity_object',
        },
        { EntityName: 'MJ_BizApps_Issues: Issue Status', OrderBy: 'Sequence ASC', ResultType: 'entity_object' },
        { EntityName: 'MJ_BizApps_Issues: Issue Types', ResultType: 'entity_object' },
        {
          EntityName: 'Channel Messages',
          ExtraFilter: `IssueID = '${this.issueId}'`,
          OrderBy: 'ReceivedAt ASC',
          ResultType: 'entity_object',
        },
        {
          EntityName: 'MJ_BizApps_Issues: Issue Comments',
          ExtraFilter: `IssueID = '${this.issueId}'`,
          OrderBy: '__mj_CreatedAt ASC',
          ResultType: 'entity_object',
        },
        {
          // Per-org ticket number (PRD §5.2) — the real org-facing {PREFIX}-#### for this ticket.
          EntityName: 'Issue Ticket Numbers',
          ExtraFilter: `IssueID = '${this.issueId}'`,
          Fields: ['TicketNumber'],
        },
      ]);

      const issues = (issueRes?.Success ? issueRes.Results : []) as mjBizAppsIssuesIssueEntity[];
      this.issue = issues[0] || null;
      // Real per-org ticket number ({PREFIX}-####); null for tickets created before per-org numbering.
      const tnRows = (ticketNumRes?.Success ? ticketNumRes.Results : []) as Array<{ TicketNumber?: string }>;
      this.ticketNumber = tnRows[0]?.TicketNumber || null;
      // Snapshot for the stale-write guard (§11.5): detect if someone else changes the ticket
      // out from under us while we have it open.
      this.loadedUpdatedAt = this.issue?.__mj_UpdatedAt ? new Date(this.issue.__mj_UpdatedAt).toISOString() : null;

      this.statuses = (statusRes?.Success ? statusRes.Results : []) as mjBizAppsIssuesIssueStatusEntity[];
      const types = (typeRes?.Success ? typeRes.Results : []) as mjBizAppsIssuesIssueTypeEntity[];
      this.status = this.issue ? this.statuses.find(s => UUIDsEqual(s.ID, this.issue!.StatusID)) || null : null;
      this.type = this.issue ? types.find(t => UUIDsEqual(t.ID, this.issue!.IssueTypeID)) || null : null;

      const messages = (msgRes?.Success ? msgRes.Results : []) as ChannelMessageEntityExtendedClient[];
      const comments = (commentRes?.Success ? commentRes.Results : []) as mjBizAppsIssuesIssueCommentEntity[];
      // Message-level internal notes on this ticket's messages. Query by the already-loaded
      // ChannelMessage IDs rather than a subquery on [Izzy].[vwChannelMessages] so
      // ClientClauseScreen.AssertUnscopedRead does not reject the filter for RLS-scoped users.
      let notes: IzzyChannelMessageNoteEntity[] = [];
      if (messages.length > 0) {
        const msgIdList = messages.map(m => `'${m.ID.replace(/'/g, "''")}'`).join(', ');
        const noteRes = await rv.RunView<IzzyChannelMessageNoteEntity>({
          EntityName: 'Channel Message Notes',
          ExtraFilter: `ChannelMessageID IN (${msgIdList})`,
          OrderBy: '__mj_CreatedAt ASC',
          ResultType: 'entity_object',
        });
        notes = (noteRes?.Success ? noteRes.Results : []) as IzzyChannelMessageNoteEntity[];
      }
      await this.resolveCommentAuthors([...comments, ...notes]);
      this.messageEntities = messages;
      this.timeline = this.buildTimeline(messages, comments, notes);
      this.issueComments = comments;
      this.messageNotes = notes;

      // Right-pane tabs: keep the agent on their tab across an in-place refresh (approving a reply
      // or changing status re-runs load()), but start on Properties when a different ticket opens.
      if (this.loadedIssueId !== this.issueId) this.activeTab = 'properties';
      this.loadedIssueId = this.issueId ?? null;
      this.resolveOrganization(messages);
      this.prepareAnalysisMessages(messages);
      this.initComposerRecipients(messages);
      // Audit history is lazy — only fetched when the agent actually opens the Timeline tab.
      this.activity = [];
      this.activityLoaded = false;
      this.activityError = '';

      await this.resolveAssignee();
      await this.resolveSLA();
      await this.resolveSuggestion();
      await this.loadMacros(messages);
      await this.loadAssignablePeople(messages);
      this.startPresence();  // §11.5: announce + subscribe to "who else is viewing"
      // If the agent is sitting on the Timeline tab, rebuild it now — the change they just made
      // (status, assignment, an approved reply) is exactly what they'd expect to see appear.
      if (this.activeTab === 'timeline') await this.loadActivity();
    } catch (error) {
      console.error('TicketDetail: failed to load ticket', error);
      this.errorMessage = 'Failed to load ticket.';
    } finally {
      this.isLoading = false;
    }
  }

  // ========== Macros (§11.6) ==========

  /**
   * Load the macros available to the acting agent for this ticket's org: Org-scoped macros, the
   * agent's own Personal macros, and macros for teams the agent belongs to. Also caches the agent's
   * first name + org name for placeholder resolution. Best-effort — macros are an enhancement.
   */
  private async loadMacros(messages: ChannelMessageEntityExtendedClient[]): Promise<void> {
    try {
      // Resolve the ticket's org from a linked ChannelMessage's channel (Issue carries no org).
      const channelId = messages.find(m => m.ChannelID)?.ChannelID;
      const channel = channelId ? IzzyMetadataEngine.Instance.Channels.find(c => UUIDsEqual(c.ID, channelId)) : null;
      const orgId = channel?.OrganizationID;
      if (!orgId) { this.macros = []; return; }
      this.orgName = channel?.Name ?? '';

      const esc = (s: string) => s.replace(/'/g, "''");
      const personId = await this.actingPersonId();
      const rv = new RunView();

      // Teams the acting agent belongs to (for Team-scope macros).
      let teamClause = '';
      if (personId) {
        const tmRes = await rv.RunView<{ TeamID: string }>({
          EntityName: 'Ticket Team Members',
          ExtraFilter: `PersonID = '${esc(personId)}' AND Status = 'Active'`,
          Fields: ['TeamID'],
        });
        const teamIds = (tmRes.Success ? tmRes.Results ?? [] : []).map(r => r.TeamID);
        if (teamIds.length) {
          teamClause = ` OR (Scope = 'Team' AND TeamID IN (${teamIds.map(id => `'${esc(id)}'`).join(', ')}))`;
        }
      }
      const personalClause = personId ? ` OR (Scope = 'Personal' AND OwnerPersonID = '${esc(personId)}')` : '';

      const res = await rv.RunView<IzzyMacroEntity>({
        EntityName: 'Macros',
        ExtraFilter:
          `OrganizationID = '${esc(orgId)}' AND Status = 'Active' AND (` +
          `Scope = 'Org'${personalClause}${teamClause})`,
        OrderBy: 'Name ASC',
        ResultType: 'entity_object',
      });
      this.macros = res.Success ? res.Results : [];

      // Cache acting agent's first name for {{agent.FirstName}}.
      if (personId) {
        const pRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
          EntityName: 'MJ_BizApps_Common: People',
          ExtraFilter: `ID = '${esc(personId)}'`,
          Fields: ['FirstName'],
          MaxRows: 1,
        });
        this.actingAgentFirstName = (pRes.Success ? pRes.Results?.[0]?.FirstName : '') ?? '';
      }

      // Cache the requester's first name for {{requester.FirstName}}.
      if (this.issue?.ReporterPersonID) {
        const rRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
          EntityName: 'MJ_BizApps_Common: People',
          ExtraFilter: `ID = '${esc(this.issue.ReporterPersonID)}'`,
          Fields: ['FirstName'],
          MaxRows: 1,
        });
        this.requesterFirstName = (rRes.Success ? rRes.Results?.[0]?.FirstName : '') ?? '';
      }
    } catch (error) {
      console.error('TicketDetail: loadMacros failed', error);
      this.macros = [];
    }
  }

  toggleMacroMenu(): void {
    this.showMacroMenu = !this.showMacroMenu;
    if (this.showMacroMenu) this.macroSearch = '';
  }

  filteredMacros(): IzzyMacroEntity[] {
    const q = this.macroSearch.trim().toLowerCase();
    if (!q) return this.macros;
    return this.macros.filter(m => m.Name.toLowerCase().includes(q));
  }

  /**
   * Insert a macro into the composer: resolve {{placeholders}}, append its Content to the reply body,
   * apply its FieldChanges (Status → transition; Priority/Severity → set + save), and bump UsageCount.
   * Field changes are best-effort and never block the text insertion.
   */
  async applyMacro(macro: IzzyMacroEntity): Promise<void> {
    this.showMacroMenu = false;
    const resolved = this.resolvePlaceholders(macro.Content || '');
    if (this.commentSource === 'outbound') {
      // Rich reply composer: insert at the caret as one undoable edit (replyBody follows via contentChange).
      this.replyEditor?.insertAtCursor(resolved);
    } else {
      this.commentBody = this.commentBody ? `${this.commentBody}\n${resolved}` : resolved;
    }

    // Apply field changes (Status/Priority/Severity), if any.
    if (macro.FieldChanges) {
      try {
        const changes = JSON.parse(macro.FieldChanges) as Record<string, string>;
        await this.applyMacroFieldChanges(changes);
      } catch (error) {
        console.error('TicketDetail: macro FieldChanges parse/apply failed', error);
      }
    }

    // Bump usage count (fire-and-forget).
    try {
      macro.UsageCount = (macro.UsageCount ?? 0) + 1;
      await macro.Save();
    } catch { /* non-critical */ }
  }

  /** Resolve the §11.6 placeholder set against the current ticket/agent/org context. */
  private resolvePlaceholders(text: string): string {
    const map: Record<string, string> = {
      // Use the displayed ticket label (TKT-####), not the raw IssueNumber (ISS-##), so a
      // customer-facing reply matches the ticket number the UI shows.
      'ticket.IssueNumber': this.ticketLabel,
      'agent.FirstName': this.actingAgentFirstName || '',
      'organization.Name': this.orgName || '',
      'requester.FirstName': this.requesterFirstName || '',
    };
    return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (whole, key: string) => (key in map ? map[key] : whole));
  }

  /** Apply a macro's field changes to the ticket. Status routes through the existing changeStatus. */
  private async applyMacroFieldChanges(changes: Record<string, string>): Promise<void> {
    if (!this.issue) return;
    if (changes['Status']) {
      const target = this.statuses.find(s => s.Name === changes['Status']);
      if (target && !UUIDsEqual(target.ID, this.issue.StatusID)) {
        await this.changeStatus(target.ID);
      }
    }
    const prio = changes['Priority'];
    const sev = changes['Severity'];
    const valid = (v: string) => ['Low', 'Medium', 'High', 'Critical'].includes(v);
    let dirty = false;
    if (prio && valid(prio)) { this.issue.Priority = prio as 'Low' | 'Medium' | 'High' | 'Critical'; dirty = true; }
    if (sev && valid(sev)) { this.issue.Severity = sev as 'Low' | 'Medium' | 'High' | 'Critical'; dirty = true; }
    if (dirty) {
      if (!await this.issue.Save()) {
        console.error('TicketDetail: macro field change save failed', this.issue.LatestResult);
      }
    }
  }

  /** Resolves the assignee (Phase 2 sets the polymorphic Issue.Assignee to a Person). */
  private async resolveAssignee(): Promise<void> {
    this.assigneeName = null;
    const recId = this.issue?.AssigneeRecordID;
    if (!recId) return;
    const rv = new RunView();
    const res = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID = '${recId}'`,
      ResultType: 'entity_object',
    });
    const p = (res.Success ? res.Results : [])[0];
    if (p) {
      this.assigneeName = p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Assigned';
    }
  }

  /**
   * Load the org's active people for the manual-assignment dropdown — everyone in the ticket's org
   * EXCEPT the acting user (who assigns to themselves via the "Assign to me" button). Org is resolved
   * from a linked ChannelMessage's channel (an Issue carries no org). No role gating — any staff
   * member can reassign.
   */
  private async loadAssignablePeople(messages: ChannelMessageEntityExtendedClient[]): Promise<void> {
    try {
      // "Me" = the engine's CurrentContactID (canonical, same as message-approval's assignment).
      this.actingPid = IzzyMetadataEngine.Instance.CurrentContactID ?? null;

      const channelId = messages.find(m => m.ChannelID)?.ChannelID;
      const channel = channelId ? IzzyMetadataEngine.Instance.Channels.find(c => UUIDsEqual(c.ID, channelId)) : null;
      const orgId = channel?.OrganizationID;
      if (!orgId) { this.assignablePeople = []; return; }

      // Mirror message-approval's assignment pool: the engine's in-memory OrganizationContacts,
      // filtered to this org + active membership (the relationship cache is loaded there), minus the
      // acting user (who self-assigns via "Assign to me"). No role gate — any staff member can assign.
      const orgContacts = IzzyMetadataEngine.Instance.OrganizationContacts
        .filter(oc => oc.OrganizationID === orgId && IzzyMetadataEngine.Instance.IsActiveMembership(oc));
      const personIds = Array.from(new Set(
        orgContacts.map(oc => oc.PersonID).filter((id): id is string => !!id && id !== this.actingPid)
      ));
      if (personIds.length === 0) { this.assignablePeople = []; return; }

      const esc = (s: string) => s.replace(/'/g, "''");
      const rv = new RunView();
      const peopleRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
        EntityName: 'MJ_BizApps_Common: People',
        ExtraFilter: `ID IN (${personIds.map(id => `'${esc(id)}'`).join(', ')})`,
        OrderBy: 'LastName ASC',
        ResultType: 'entity_object',
      });
      this.assignablePeople = (peopleRes.Success ? peopleRes.Results : [])
        .map(p => ({ id: p.ID, name: p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Unknown' }));
    } catch (error) {
      console.error('TicketDetail: loadAssignablePeople failed', error);
      this.assignablePeople = [];
    }
  }

  /** Whether the ticket is currently assigned to the acting user (controls the "Assign to me" state). */
  protected get isAssignedToMe(): boolean {
    return !!this.actingPid && !!this.issue?.AssigneeRecordID && UUIDsEqual(this.issue.AssigneeRecordID, this.actingPid);
  }

  /**
   * Set (or clear, when personId is '') the ticket's assignee. Direct entity mutate + Save, mirroring
   * changeStatus (the browser can't run the server assign action). No role gate — any staff member.
   */
  async changeAssignee(personId: string): Promise<void> {
    if (!this.issue || this.isAssigning) return;
    if (!await this.confirmNotStale()) return;
    this.isAssigning = true;
    try {
      const md = new Metadata();
      const peopleEntityId = md.EntityByName('MJ_BizApps_Common: People')?.ID || null;
      if (personId) {
        if (!peopleEntityId) { this.errorMessage = 'Could not resolve the People entity.'; return; }
        this.issue.AssigneeEntityID = peopleEntityId;
        this.issue.AssigneeRecordID = personId;
      } else {
        this.issue.AssigneeEntityID = null;
        this.issue.AssigneeRecordID = null;
      }
      if (!await this.issue.Save()) {
        this.errorMessage = 'Failed to change assignee.';
        return;
      }
      await this.resolveAssignee();
    } catch (error) {
      console.error('TicketDetail: changeAssignee failed', error);
      this.errorMessage = 'Failed to change assignee.';
    } finally {
      this.isAssigning = false;
    }
  }

  /** One-click self-assign for the acting user (the §9.1 self-assign / claim case). */
  async assignToMe(): Promise<void> {
    if (this.actingPid) {
      await this.changeAssignee(this.actingPid);
    }
  }

  // ========== Collision detection / presence (§11.5) ==========

  private static readonly PRESENCE_HEARTBEAT_MS = 15_000;
  private static readonly PRESENCE_TTL_MS = 35_000;  // a viewer not heard from in this window is dropped

  // Identity and scope are owned by the server (PresenceResolver): we say which ticket, it says who we are.
  private readonly PRESENCE_SUB = `
    subscription Presence($organizationID: String!, $scope: String!, $recordID: String!) {
      presence(organizationID: $organizationID, scope: $scope, recordID: $recordID) { RecordID PersonID PersonName Action At }
    }`;
  private readonly PRESENCE_PUB = `
    mutation AnnouncePresence($scope: String!, $recordID: String!, $action: String!) {
      announcePresence(scope: $scope, recordID: $recordID, action: $action)
    }`;

  /** Begin announcing + listening for presence on the current ticket. */
  private startPresence(): void {
    const issueId = this.issue?.ID;
    if (!issueId || !this.actingPid || !this.organizationId) return;  // need a ticket, its org, and our own identity
    this.presenceIssueId = issueId;
    this.presenceMap.clear();
    this.otherViewers = [];

    const provider = Metadata.Provider as GraphQLDataProvider;
    try {
      const vars = { organizationID: this.organizationId, scope: 'ticket', recordID: issueId };
      this.presenceSub = provider.subscribe(this.PRESENCE_SUB, vars).subscribe({
        next: (data: { presence?: { PersonID: string; PersonName?: string; Action: string } }) => {
          const n = data?.presence;
          if (!n || n.PersonID === this.actingPid) return;  // ignore our own echoes
          if (n.Action === 'left') {
            this.presenceMap.delete(n.PersonID);
          } else {
            this.presenceMap.set(n.PersonID, { name: n.PersonName || 'A teammate', lastSeen: Date.now() });
          }
          this.refreshViewers();
        },
        error: (e: unknown) => console.error('TicketDetail: presence subscription error', e),
      });
    } catch (e) {
      console.error('TicketDetail: failed to subscribe to presence', e);
    }

    // Announce immediately, then heartbeat; sweep expires stale viewers.
    void this.announcePresence('viewing');
    this.presenceHeartbeat = setInterval(() => void this.announcePresence('viewing'), TicketDetailComponent.PRESENCE_HEARTBEAT_MS);
    this.presenceSweep = setInterval(() => this.sweepStaleViewers(), TicketDetailComponent.PRESENCE_HEARTBEAT_MS);
  }

  /** Stop presence: announce 'left', unsubscribe, clear timers. Safe to call repeatedly. */
  private stopPresence(): void {
    if (this.presenceIssueId && this.actingPid) {
      void this.announcePresence('left', this.presenceIssueId);
    }
    this.presenceSub?.unsubscribe();
    this.presenceSub = undefined;
    if (this.presenceHeartbeat) { clearInterval(this.presenceHeartbeat); this.presenceHeartbeat = undefined; }
    if (this.presenceSweep) { clearInterval(this.presenceSweep); this.presenceSweep = undefined; }
    this.presenceMap.clear();
    this.otherViewers = [];
    this.presenceIssueId = null;
  }

  /** Publish a presence heartbeat/leave for the acting user on a ticket. */
  private async announcePresence(action: 'viewing' | 'left', issueId?: string): Promise<void> {
    const id = issueId || this.issue?.ID;
    if (!id || !this.actingPid) return;
    try {
      const provider = Metadata.Provider as GraphQLDataProvider;
      await provider.ExecuteGQL(this.PRESENCE_PUB, { scope: 'ticket', recordID: id, action });
    } catch (e) {
      console.error('TicketDetail: announcePresence failed', e);
    }
  }

  /** Drop viewers we haven't heard a heartbeat from within the TTL (closed tab / dropped connection). */
  private sweepStaleViewers(): void {
    const cutoff = Date.now() - TicketDetailComponent.PRESENCE_TTL_MS;
    let changed = false;
    for (const [pid, v] of this.presenceMap) {
      if (v.lastSeen < cutoff) { this.presenceMap.delete(pid); changed = true; }
    }
    if (changed) this.refreshViewers();
  }

  private refreshViewers(): void {
    this.otherViewers = Array.from(this.presenceMap.values()).map(v => v.name);
  }

  /**
   * Stale-write guard (§11.5): before a write, re-check the ticket's UpdatedAt against the snapshot
   * taken at load. If someone else changed it meanwhile, ask the user to confirm before overwriting.
   * Returns true to proceed (and refreshes the snapshot so subsequent writes don't re-prompt), false
   * to abort. Fail-open: if the check itself errors, allow the write (never block on a guard failure).
   */
  private async confirmNotStale(): Promise<boolean> {
    if (!this.issue) return true;
    try {
      const rv = new RunView();
      const res = await rv.RunView<{ __mj_UpdatedAt: Date }>({
        EntityName: 'MJ_BizApps_Issues: Issues',
        ExtraFilter: `ID = '${this.issue.ID.replace(/'/g, "''")}'`,
        Fields: ['__mj_UpdatedAt'],
        MaxRows: 1,
      });
      const current = res.Success && res.Results?.[0]?.__mj_UpdatedAt
        ? new Date(res.Results[0].__mj_UpdatedAt).toISOString() : null;
      if (current && this.loadedUpdatedAt && current !== this.loadedUpdatedAt) {
        const proceed = confirm('This ticket was changed by someone else since you opened it. Save your change anyway? (Reload to see their changes.)');
        if (!proceed) return false;
        this.loadedUpdatedAt = current;  // accept the new baseline so we don't re-prompt
      }
      return true;
    } catch (e) {
      console.error('TicketDetail: stale-write check failed (allowing write)', e);
      return true;
    }
  }

  /** Loads the ticket's SLA state (1:1 by IssueID); null when the ticket has no SLA clock. */
  private async resolveSLA(): Promise<void> {
    this.sla = null;
    if (!this.issue) return;
    const rv = new RunView();
    const res = await rv.RunView<IzzyTicketSLAStateEntity>({
      EntityName: 'Ticket SLA States',
      ExtraFilter: `IssueID = '${this.issue.ID}'`,
      ResultType: 'entity_object',
    });
    this.sla = (res.Success ? res.Results : [])[0] || null;
  }

  // ========== Escalation (§10.6) ==========

  /** Derived escalation state — single definition in TicketState, shared with the queue. */
  protected get isEscalated(): boolean {
    return TicketState.IsTicketEscalated(this.messageEntities, this.status, this.issue?.Status);
  }

  /** Escalation rides on a message, so it needs an eligible carrier: no linked messages, or
   *  nothing but Approved (sent) responses, means no escalation — the Escalate button hides
   *  (same idiom as an already-escalated ticket). Eligibility is TicketState policy. */
  protected get canEscalate(): boolean {
    return !this.isEscalated && TicketState.PickEscalationTarget(this.messageEntities) !== null;
  }

  protected openEscalatePanel(): void {
    this.showEscalatePanel = true;
    this.escalateReason = '';
    this.escalateError = '';
  }

  protected closeEscalatePanel(): void {
    this.showEscalatePanel = false;
  }

  /**
   * Escalate the ticket: mark its most relevant message Escalated (target selection is
   * TicketState policy, not a component decision). The server's Save() override stamps who
   * escalated from the request context and drives the ticket itself (§10.6: In Progress
   * transition, escalation-team routing, internal note, issue.escalated event), so a reload
   * right after shows the full effect.
   */
  protected async confirmEscalate(): Promise<void> {
    if (!this.canEscalate || this.isEscalating) return;
    const target = TicketState.PickEscalationTarget(this.messageEntities);
    if (!target) return;

    this.isEscalating = true;
    this.escalateError = '';
    try {
      // Attribution (who escalated) is stamped server-side from the request context.
      target.Escalate(this.escalateReason.trim() || undefined);
      if (!await target.Save()) {
        this.escalateError = 'Failed to escalate this ticket. Please try again.';
        return;
      }
      this.showEscalatePanel = false;
      // Reload to pick up the server-side cascade (status transition, routing, internal note).
      // The cascade is fire-and-forget on the server, so give it a beat before refetching —
      // the Escalated badge itself doesn't depend on this (the message state is already saved).
      await new Promise(resolve => setTimeout(resolve, 1500));
      await this.load();
    } catch (error) {
      console.error('TicketDetail: escalate failed', error);
      this.escalateError = 'Failed to escalate this ticket. Please try again.';
    } finally {
      this.isEscalating = false;
    }
  }

  protected openExportPanel(): void {
    this.showExportPanel = true;
    this.exportIncludeInternalNotes = false;
    this.exportError = '';
  }

  protected closeExportPanel(): void {
    if (this.isExporting) return;
    this.showExportPanel = false;
  }

  /**
   * Export the ticket as a PDF via the server-side Export Ticket PDF action (the browser
   * can't render one). The action rebuilds the same shared timeline this screen shows and
   * returns the document base64-encoded; skipActionLog keeps the multi-MB payload out of
   * ActionExecutionLog (the action leaves a compact server-side log line instead).
   */
  protected async confirmExport(): Promise<void> {
    if (!this.issueId || this.isExporting) return;
    this.isExporting = true;
    this.exportError = '';
    try {
      const result = await this.runAction('Export Ticket PDF', [
        { Name: 'IssueID', Value: this.issueId, Type: 'Input' },
        { Name: 'IncludeInternalNotes', Value: this.exportIncludeInternalNotes ? 'true' : 'false', Type: 'Input' },
      ], true);
      if (!result.ok) {
        this.exportError = result.message || 'Export failed. Please try again.';
        return;
      }
      const base64 = result.outputs.find(o => o.Name === 'PDFBase64')?.Value;
      const fileName = result.outputs.find(o => o.Name === 'FileName')?.Value;
      if (typeof base64 !== 'string' || base64.length === 0) {
        this.exportError = 'Export returned no document. Please try again.';
        return;
      }
      DownloadBase64File(base64, typeof fileName === 'string' && fileName ? fileName : `Ticket-${this.ticketLabel}.pdf`);
      this.showExportPanel = false;
    } catch (error) {
      console.error('TicketDetail: export failed', error);
      this.exportError = 'Export failed. See console for details.';
    } finally {
      this.isExporting = false;
    }
  }

  // ========== Right-pane tabs ==========

  /** Switch the right pane. The Timeline tab fetches its audit history on first open. */
  protected setTab(tab: 'properties' | 'timeline' | 'analysis'): void {
    this.activeTab = tab;
    if (tab === 'timeline' && !this.activityLoaded && !this.isActivityLoading) {
      void this.loadActivity();
    }
  }

  // ========== AI Analysis tab ==========

  /** Resolve the ticket's owning org from a linked message's channel (an Issue carries no org). */
  private resolveOrganization(messages: ChannelMessageEntityExtendedClient[]): void {
    const channelId = messages.find(m => m.ChannelID)?.ChannelID;
    const channel = channelId
      ? IzzyMetadataEngine.Instance.Channels.find(c => UUIDsEqual(c.ID, channelId))
      : null;
    this.organizationId = channel?.OrganizationID ?? null;
  }

  /**
   * Pick the messages worth analysing and choose the default one: the reply the agent is most
   * likely about to act on (the pending one), else the most recent analysed message — so opening
   * the tab lands on something useful without a click.
   */
  private prepareAnalysisMessages(messages: ChannelMessageEntityExtendedClient[]): void {
    this.analysisMessages = messages.filter(m => this.hasAnalysis(m));
    const pending = this.analysisMessages.find(m => m.ApprovalStatus === 'Pending' && !!m.GeneratedReplyContent);
    const latest = this.analysisMessages[this.analysisMessages.length - 1];
    this.selectedAnalysisMessageId = (pending ?? latest)?.ID ?? null;
    this.isRescoring = false;
    this.isReprocessing = false;
  }

  /** A message has analysis when the evaluator scored it, skipped it, or failed on it. */
  private hasAnalysis(m: ChannelMessageEntityExtendedClient): boolean {
    return (m.AIConfidenceScore !== null && m.AIConfidenceScore !== undefined)
      || !!m.AIConfidenceReason || !!m.SkipReasoning || !!m.ErrorType;
  }

  /** The message the analysis pane is currently showing. */
  protected get selectedAnalysisMessage(): ChannelMessageEntityExtendedClient | null {
    const id = this.selectedAnalysisMessageId;
    if (!id) return null;
    return this.analysisMessages.find(m => UUIDsEqual(m.ID, id)) ?? null;
  }

  /** Dropdown label for one analysable message: who it was with, when, and the score. */
  protected analysisMessageLabel(m: ChannelMessageEntityExtendedClient): string {
    const when = m.ReceivedAt || m.__mj_CreatedAt;
    const date = when ? new Date(when).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
    // -1 is TriggerRescore's sentinel, not a real score.
    const score = typeof m.AIConfidenceScore === 'number' && m.AIConfidenceScore >= 0
      ? ` · ${m.AIConfidenceScore}`
      : '';
    return `${m.Sender || 'Customer'}${date ? ` · ${date}` : ''}${score}`;
  }

  protected get canRescoreSelected(): boolean {
    const m = this.selectedAnalysisMessage;
    if (!m) return false;
    if (m.GenerationStatus === 'Creating Reply') return false;  // UI-state concern, as on Review & Approve
    return m.CanRescore();
  }

  /**
   * Re-run the confidence evaluator on the selected message. Same path as Review & Approve: the
   * entity subclass sets the sentinel, the server's Save() override does the work, and we poll
   * because the rescore completes asynchronously.
   */
  protected async onRescoreRequested(): Promise<void> {
    const message = this.selectedAnalysisMessage;
    if (!message || !this.canRescoreSelected || this.isReprocessing) return;
    this.isReprocessing = true;
    this.isRescoring = true;
    this.errorMessage = '';
    try {
      message.TriggerRescore();
      const saved = await message.Save();
      if (!saved) {
        this.errorMessage = 'Failed to save message state for re-evaluation.';
        this.isReprocessing = false;
        this.isRescoring = false;
        return;
      }
      await this.pollForRescoreResult(message.ID);
    } catch (error) {
      console.error('TicketDetail: rescore failed', error);
      this.errorMessage = 'Failed to re-evaluate this reply. Please try again.';
      this.isReprocessing = false;
      this.isRescoring = false;
    }
  }

  /** Poll until the server lands a fresh score on the message (or we stop waiting). */
  private async pollForRescoreResult(messageId: string): Promise<void> {
    const POLL_INTERVAL = 2500;
    const MAX_POLLS = 24;  // 60s total, matching Review & Approve
    const esc = (s: string) => s.replace(/'/g, "''");
    let polls = 0;

    const poll = async (): Promise<void> => {
      polls++;
      try {
        const rv = new RunView();
        const res = await rv.RunView<ChannelMessageEntityExtendedClient>({
          EntityName: 'Channel Messages',
          ExtraFilter: `ID = '${esc(messageId)}'`,
          ResultType: 'entity_object',
        });
        const fresh = (res.Success ? res.Results : [])[0];
        // A real (non-sentinel) score means the evaluator has finished.
        if (fresh && typeof fresh.AIConfidenceScore === 'number' && fresh.AIConfidenceScore >= 0) {
          const target = this.analysisMessages.find(m => UUIDsEqual(m.ID, messageId));
          if (target) {
            target.AIConfidenceScore = fresh.AIConfidenceScore;
            target.AIConfidenceReason = fresh.AIConfidenceReason;
            target.AIEvaluatedAt = fresh.AIEvaluatedAt;
            target.ApprovalStatus = fresh.ApprovalStatus;
          }
          this.isReprocessing = false;
          this.isRescoring = false;
          return;
        }
      } catch (error) {
        console.error('TicketDetail: rescore poll failed', error);
      }

      if (polls >= MAX_POLLS) {
        this.isReprocessing = false;
        this.isRescoring = false;
        this.errorMessage = 'Re-evaluation is still processing. Reopen the ticket to see the updated score.';
        return;
      }
      setTimeout(() => void poll(), POLL_INTERVAL);
    };

    await poll();
  }

  // ========== Timeline tab (ticket lifecycle audit) ==========

  /**
   * Build the ticket's lifecycle history — the part the Conversation pane does NOT show. Sources:
   *  - `__mj.RecordChange` for the Issue (TrackRecordChanges=1): real status / priority / assignee
   *    transitions with old → new values and who made them.
   *  - The SLA clock's discrete moments (first response, warning, breach, pause).
   *  - ONE compact line per message and per note/comment — the Conversation pane owns the bodies,
   *    so this stays an audit trail rather than a second transcript.
   */
  private async loadActivity(): Promise<void> {
    if (!this.issue) return;
    this.isActivityLoading = true;
    this.activityError = '';
    try {
      const entries: ActivityEntry[] = [
        {
          when: new Date(this.issue.__mj_CreatedAt),
          icon: 'fa-solid fa-ticket',
          label: `Ticket created · ${this.ticketLabel}`,
          detail: this.issue.ReporterEmail ? `Reported by ${this.issue.ReporterEmail}` : undefined,
          kind: 'created',
        },
        ...await this.loadFieldChanges(),
        ...this.buildMessageActivity(),
        ...this.buildCommentActivity(),
        ...this.buildSLAActivity(),
      ];

      this.activity = entries.sort((a, b) => a.when.getTime() - b.when.getTime());
      this.activityLoaded = true;
    } catch (error) {
      console.error('TicketDetail: failed to load activity', error);
      this.activityError = 'Failed to load ticket history.';
    } finally {
      this.isActivityLoading = false;
    }
  }

  /**
   * Field-change history from `__mj.RecordChange`. ChangesJSON carries {field, oldValue, newValue}
   * per changed column; we render only the columns an agent cares about, and resolve the UUIDs
   * (StatusID, AssigneeRecordID) to names — a raw GUID transition is unreadable.
   */
  private async loadFieldChanges(): Promise<ActivityEntry[]> {
    const issue = this.issue;
    if (!issue) return [];
    // RecordChange rows are keyed by entity + record, so we need the Issues entity's ID.
    const entityId = new Metadata().EntityByName('MJ_BizApps_Issues: Issues')?.ID;
    if (!entityId) return [];

    const esc = (s: string) => s.replace(/'/g, "''");
    const rv = new RunView();
    const res = await rv.RunView<MJRecordChangeEntity>({
      EntityName: 'MJ: Record Changes',
      ExtraFilter: `EntityID = '${esc(entityId)}' AND RecordID = '${esc(issue.ID)}'`,
      OrderBy: 'ChangedAt ASC',
      ResultType: 'entity_object',
    });
    if (!res.Success) return [];

    // 'Create' is already covered by the "Ticket created" entry; Delete/Snapshot aren't ticket events.
    const updates = res.Results.filter(c => c.Type === 'Update');
    await this.cachePersonNamesFor(updates);

    const entries: ActivityEntry[] = [];
    for (const change of updates) {
      const who = change.User ? `by ${change.User}` : undefined;
      for (const line of this.describeFieldChanges(change.ChangesJSON)) {
        entries.push({ when: new Date(change.ChangedAt), icon: line.icon, label: line.label, detail: who, kind: line.kind });
      }
    }
    return entries;
  }

  /** Parsed shape of one RecordChange.ChangesJSON entry. */
  private parseChangesJSON(json: string | null): Record<string, { oldValue?: unknown; newValue?: unknown }> | null {
    if (!json) return null;
    try {
      return JSON.parse(json) as Record<string, { oldValue?: unknown; newValue?: unknown }>;
    } catch {
      return null;  // Malformed history shouldn't break the tab.
    }
  }

  /** Pre-resolve every person referenced by an assignee change, so lines read as names not GUIDs. */
  private async cachePersonNamesFor(changes: MJRecordChangeEntity[]): Promise<void> {
    const ids = new Set<string>();
    for (const c of changes) {
      const assignee = this.parseChangesJSON(c.ChangesJSON)?.['AssigneeRecordID'];
      for (const value of [assignee?.oldValue, assignee?.newValue]) {
        if (typeof value === 'string' && value) ids.add(value);
      }
    }
    const missing = Array.from(ids).filter(id => !this.activityPersonNames.has(id));
    if (missing.length === 0) return;

    const esc = (s: string) => s.replace(/'/g, "''");
    const res = await new RunView().RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${missing.map(id => `'${esc(id)}'`).join(', ')})`,
      ResultType: 'entity_object',
    });
    for (const p of (res.Success ? res.Results : [])) {
      const name = p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Someone';
      this.activityPersonNames.set(p.ID, name);
    }
  }

  /** Turn one RecordChange's ChangesJSON into readable lines, skipping plumbing columns. */
  private describeFieldChanges(changesJson: string | null): Array<{ label: string; icon: string; kind: ActivityEntry['kind'] }> {
    const parsed = this.parseChangesJSON(changesJson);
    if (!parsed) return [];

    const lines: Array<{ label: string; icon: string; kind: ActivityEntry['kind'] }> = [];
    for (const [field, change] of Object.entries(parsed)) {
      const from = change?.oldValue;
      const to = change?.newValue;
      switch (field) {
        case 'StatusID':
          lines.push({
            label: `Status  ${this.statusName(from)} → ${this.statusName(to)}`,
            icon: 'fa-solid fa-arrow-right-arrow-left',
            kind: 'field',
          });
          break;
        case 'Priority':
        case 'Severity':
          lines.push({
            label: `${field}  ${this.plainValue(from)} → ${this.plainValue(to)}`,
            icon: 'fa-solid fa-flag',
            kind: 'field',
          });
          break;
        case 'AssigneeRecordID':
          lines.push({
            label: typeof to === 'string' && to ? `Assigned to ${this.activityPersonName(to)}` : 'Unassigned',
            icon: 'fa-solid fa-user-check',
            kind: 'field',
          });
          break;
        case 'IssueTypeID':
          lines.push({ label: 'Type changed', icon: 'fa-solid fa-tag', kind: 'field' });
          break;
        case 'Title':
          lines.push({ label: 'Title edited', icon: 'fa-solid fa-pen', kind: 'field' });
          break;
        case 'ResolvedAt':
          if (to) lines.push({ label: 'Resolved', icon: 'fa-solid fa-circle-check', kind: 'closed' });
          break;
        case 'ClosedAt':
          if (to) lines.push({ label: 'Closed', icon: 'fa-solid fa-lock', kind: 'closed' });
          break;
        default:
          // Everything else (AssigneeEntityID plumbing, reporter back-fills, timestamps) is noise here.
          break;
      }
    }
    return lines;
  }

  /** Status display name for a raw StatusID out of the change history. */
  private statusName(value: unknown): string {
    if (typeof value !== 'string' || !value) return '—';
    return this.statuses.find(s => UUIDsEqual(s.ID, value))?.Name ?? 'Unknown';
  }

  /** Person display name for an ID seen in the change history. */
  private activityPersonName(id: string): string {
    return this.activityPersonNames.get(id)
      ?? this.assignablePeople.find(p => UUIDsEqual(p.id, id))?.name
      ?? 'another agent';
  }

  private plainValue(value: unknown): string {
    return value === null || value === undefined || value === '' ? '—' : String(value);
  }

  /** One compact line per message event — the Conversation pane owns the bodies. */
  private buildMessageActivity(): ActivityEntry[] {
    const entries: ActivityEntry[] = [];
    for (const m of this.messageEntities) {
      const channel = IzzyMetadataEngine.Instance.Channels.find(c => UUIDsEqual(c.ID, m.ChannelID));
      const via = channel ? IzzyMetadataEngine.Instance.GetChannelType(channel.ChannelTypeID)?.Name || '' : '';
      // A staff outbound reply has SentContent but no inbound half (mirrors buildTimeline).
      const isOutboundOnly = !m.MessageContent && !!m.SentContent;
      // A staff PENDING draft (Manual Messages, #289): OutboundType stamped at creation,
      // nothing sent yet — never a phantom inbound.
      const isStaffPendingDraft = !m.MessageContent && !m.SentContent &&
        !!m.OutboundType && m.OutboundType !== 'Forward';

      if (isStaffPendingDraft) {
        entries.push({
          when: new Date(m.__mj_CreatedAt),
          icon: 'fa-solid fa-pen-to-square',
          label: `Staff draft to ${m.Recipient || 'customer'} — pending approval`,
          detail: via || undefined,
          kind: 'message',
        });
      } else if (!isOutboundOnly) {
        entries.push({
          when: new Date(m.ReceivedAt || m.__mj_CreatedAt),
          icon: 'fa-solid fa-inbox',
          label: `Inbound message from ${m.Sender || 'customer'}`,
          detail: via || undefined,
          kind: 'message',
        });
      }

      if (m.GeneratedReplyContent && m.AIEvaluatedAt) {
        const score = typeof m.AIConfidenceScore === 'number' && m.AIConfidenceScore >= 0
          ? ` (score ${m.AIConfidenceScore})`
          : '';
        entries.push({
          when: new Date(m.AIEvaluatedAt),
          icon: 'fa-solid fa-robot',
          label: `Izzy drafted a reply${score}`,
          kind: 'message',
        });
      }

      if (m.GenerationStatus === 'Skipped' && m.AIEvaluatedAt) {
        entries.push({
          when: new Date(m.AIEvaluatedAt),
          icon: 'fa-solid fa-forward',
          label: 'Izzy skipped this message',
          detail: m.SkipCategory || undefined,
          kind: 'message',
        });
      }

      if (m.EscalatedAt) {
        entries.push({
          when: new Date(m.EscalatedAt),
          icon: 'fa-solid fa-triangle-exclamation',
          label: 'Escalated for human review',
          detail: m.ApprovalFeedback || undefined,
          kind: 'escalated',
        });
      }

      if (m.ApprovedAt && m.ApprovalStatus === 'Approved') {
        entries.push({
          when: new Date(m.ApprovedAt),
          icon: 'fa-solid fa-check',
          label: m.AIAutoApproved ? 'Reply auto-approved' : 'Reply approved',
          detail: m.AIAutoApproved ? 'Score was at or above the channel threshold' : undefined,
          kind: 'message',
        });
      }

      if (m.ApprovedAt && m.ApprovalStatus === 'Rejected') {
        entries.push({
          when: new Date(m.ApprovedAt),
          icon: 'fa-solid fa-xmark',
          label: 'Reply rejected',
          kind: 'message',
        });
      }

      if (m.SentAt) {
        const isForward = m.OutboundType === 'Forward';
        const recipients = FormatSentRecipients(m);
        entries.push({
          when: new Date(m.SentAt),
          icon: isForward ? 'fa-solid fa-share' : 'fa-solid fa-paper-plane',
          label: isForward ? 'Message forwarded' : (isOutboundOnly ? 'Staff reply sent' : 'Reply sent to customer'),
          detail: recipients ? [via, recipients].filter(Boolean).join(' — ') : (via || undefined),
          kind: 'message',
        });
      }
    }
    return entries;
  }

  /** A line per note/comment (ticket-level and message-level) — again, body stays in the Conversation pane. */
  private buildCommentActivity(): ActivityEntry[] {
    const commentLines: ActivityEntry[] = this.issueComments.map(c => ({
      when: new Date(c.__mj_CreatedAt),
      icon: c.Source === 'internal' ? 'fa-solid fa-lock' : 'fa-solid fa-comment',
      label: c.Source === 'internal' ? 'Internal note added' : 'Comment added',
      detail: this.commentAuthorNames.get(c.ID) || c.AuthorEmail || undefined,
      kind: 'comment' as const,
    }));
    const noteLines: ActivityEntry[] = this.messageNotes.map(n => ({
      when: new Date(n.__mj_CreatedAt),
      icon: 'fa-solid fa-lock',
      label: 'Internal note added on a message',
      detail: this.commentAuthorNames.get(n.ID) || n.AuthorEmail || undefined,
      kind: 'comment' as const,
    }));
    return [...commentLines, ...noteLines];
  }

  /** The SLA clock's discrete moments. Ticket SLA States holds current state, not history. */
  private buildSLAActivity(): ActivityEntry[] {
    const sla = this.sla;
    if (!sla) return [];
    const entries: ActivityEntry[] = [];

    if (sla.FirstResponseAt) {
      entries.push({
        when: new Date(sla.FirstResponseAt),
        icon: 'fa-solid fa-stopwatch',
        label: 'First response sent',
        detail: sla.FirstResponseDueAt
          ? `Target was ${new Date(sla.FirstResponseDueAt).toLocaleString()}`
          : undefined,
        kind: 'sla',
      });
    }
    if (sla.WarningFiredAt) {
      entries.push({
        when: new Date(sla.WarningFiredAt),
        icon: 'fa-solid fa-triangle-exclamation',
        label: 'SLA warning — approaching the target',
        kind: 'sla',
      });
    }
    if (sla.SLAPausedAt) {
      entries.push({
        when: new Date(sla.SLAPausedAt),
        icon: 'fa-solid fa-pause',
        label: 'SLA clock paused',
        detail: sla.SLAPausedMinutes ? `${sla.SLAPausedMinutes} minutes paused so far` : undefined,
        kind: 'sla',
      });
    }
    if (sla.Breached && sla.BreachedAt) {
      entries.push({
        when: new Date(sla.BreachedAt),
        icon: 'fa-solid fa-circle-exclamation',
        label: 'SLA breached',
        kind: 'sla',
      });
    }
    return entries;
  }

  /**
   * Loads any OPEN follow-up suggestion where THIS ticket is the newly-created one (NewIssueID).
   * The agent records these when an inbound follow-up came from a reporter who has several open
   * tickets — too ambiguous to auto-link, so we surface "this may be a follow-up to ISS-XX" for
   * staff to confirm (merge) or dismiss. Resolves the suggested ticket's number for the banner.
   */
  private async resolveSuggestion(): Promise<void> {
    this.suggestion = null;
    this.suggestionInfo = null;
    if (!this.issue) return;
    const esc = (s: string) => s.replace(/'/g, "''");
    const rv = new RunView();
    const res = await rv.RunView<IzzyTicketSuggestionEntity>({
      EntityName: 'Ticket Suggestions',
      ExtraFilter: `NewIssueID = '${esc(this.issue.ID)}' AND Status = 'Open'`,
      OrderBy: '__mj_CreatedAt DESC',
      MaxRows: 1,
      ResultType: 'entity_object',
    });
    const sug = (res.Success ? res.Results : [])[0] || null;
    if (!sug) return;

    // Resolve the suggested ticket's number for display.
    const issueRes = await rv.RunView<mjBizAppsIssuesIssueEntity>({
      EntityName: 'MJ_BizApps_Issues: Issues',
      ExtraFilter: `ID = '${esc(sug.SuggestedIssueID)}'`,
      Fields: ['ID', 'IssueNumber'],
      MaxRows: 1,
    });
    const suggested = (issueRes.Success ? issueRes.Results : [])[0];
    // Only show the banner if the suggested ticket still exists/loads.
    if (!suggested) return;

    this.suggestion = sug;
    this.suggestionInfo = {
      issueNumber: this.formatTicketLabel(suggested.IssueNumber),
      reason: sug.Reason || 'A similar open ticket from the same person was found.',
    };
  }

  /** Mirror of ticketLabel for an arbitrary IssueNumber (the suggested ticket). */
  private formatTicketLabel(issueNumber: string | null | undefined): string {
    const num = issueNumber || '';
    const seq = num.includes('-') ? num.split('-').pop() : num;
    return seq ? `TKT-${seq.padStart(4, '0')}` : 'TKT-?';
  }

  /**
   * Accept the suggestion: merge THIS (new) ticket INTO the suggested existing ticket — the same
   * tombstone merge the manual merge panel runs (loser = this ticket, survivor = suggested). On
   * success, mark the suggestion Accepted; the merge service tombstones this ticket, so we then
   * leave the page (and offer the reporter-merge prompt if the reporters differ).
   */
  async acceptSuggestion(): Promise<void> {
    if (!this.issue || !this.suggestion) return;
    this.isResolvingSuggestion = true;
    this.mergeError = '';
    const loserIssueId = this.issue.ID;
    const loserReporter = this.issue.ReporterPersonID ?? null;
    const survivorId = this.suggestion.SuggestedIssueID;
    const survivorLabel = this.suggestionInfo?.issueNumber || 'the other ticket';
    try {
      const result = await this.runAction('Merge Tickets', [
        { Name: 'SurvivorIssueID', Value: survivorId, Type: 'Input' },
        { Name: 'LoserIssueID', Value: loserIssueId, Type: 'Input' },
      ]);
      if (!result.ok) {
        if (result.message) console.warn('Suggested merge failed:', result.message);
        this.mergeError = `Couldn't merge this ticket into ${survivorLabel}. Please try again, or contact support if it keeps happening.`;
        return;
      }
      await this.resolveSuggestionStatus('Accepted');

      // Reuse the reporter-merge prompt when the two tickets had different reporters.
      const survivorReporter = await this.issueReporter(survivorId);
      if (loserReporter && survivorReporter && !UUIDsEqual(loserReporter, survivorReporter)) {
        const [survivorName, loserName] = await Promise.all([
          this.personName(survivorReporter),
          this.personName(loserReporter),
        ]);
        this.reporterMerge = { survivorPersonId: survivorReporter, loserPersonId: loserReporter, survivorName, loserName };
        this.showReporterMergePrompt = true;
        this.suggestion = null;
      } else {
        this.back.emit();
      }
    } catch (error) {
      console.error('TicketDetail: suggested merge failed', error);
      this.mergeError = 'Merge failed. See console for details.';
    } finally {
      this.isResolvingSuggestion = false;
    }
  }

  /** Dismiss the suggestion — keep this ticket separate; the banner goes away. */
  async dismissSuggestion(): Promise<void> {
    if (!this.suggestion) return;
    this.isResolvingSuggestion = true;
    try {
      await this.resolveSuggestionStatus('Dismissed');
      this.suggestion = null;
      this.suggestionInfo = null;
    } catch (error) {
      console.error('TicketDetail: failed to dismiss suggestion', error);
    } finally {
      this.isResolvingSuggestion = false;
    }
  }

  /** Stamp the suggestion's terminal status + resolved-at, and save. */
  private async resolveSuggestionStatus(status: 'Accepted' | 'Dismissed'): Promise<void> {
    if (!this.suggestion) return;
    this.suggestion.Status = status;
    this.suggestion.ResolvedAt = new Date();
    if (!await this.suggestion.Save()) {
      console.warn('TicketDetail: failed to update suggestion status', this.suggestion.LatestResult);
    }
  }

  /** ReporterPersonID for an Issue by ID, or null. */
  private async issueReporter(issueId: string): Promise<string | null> {
    const esc = (s: string) => s.replace(/'/g, "''");
    const rv = new RunView();
    const res = await rv.RunView<mjBizAppsIssuesIssueEntity>({
      EntityName: 'MJ_BizApps_Issues: Issues',
      ExtraFilter: `ID = '${esc(issueId)}'`,
      Fields: ['ID', 'ReporterPersonID'],
      MaxRows: 1,
    });
    return (res.Success ? res.Results?.[0]?.ReporterPersonID : null) ?? null;
  }

  /** Badge kind for the SLA properties row: breached / warning / ontrack / none. */
  get slaKind(): 'breached' | 'warning' | 'ontrack' | 'none' {
    if (!this.sla) return 'none';
    if (this.sla.Breached) return 'breached';
    const due = this.sla.ResolutionDueAt ? new Date(this.sla.ResolutionDueAt).getTime() : null;
    if (due == null) return 'ontrack';
    const remaining = due - Date.now();
    if (remaining <= 0) return 'breached';
    if (this.sla.WarningFiredAt || remaining <= 30 * 60_000) return 'warning';
    return 'ontrack';
  }

  /** Human label for the SLA properties row. */
  get slaLabel(): string {
    if (!this.sla) return 'No SLA';
    if (this.sla.Breached) return 'Breached';
    const due = this.sla.ResolutionDueAt ? new Date(this.sla.ResolutionDueAt) : null;
    if (!due) return 'On track';
    const remaining = due.getTime() - Date.now();
    if (remaining <= 0) return 'Overdue';
    const min = Math.max(1, Math.round(remaining / 60_000));
    const label = min < 60 ? `${min}m` : `${Math.floor(min / 60)}h ${min % 60}m`;
    return `Due in ${label}`;
  }

  /**
   * Builds the unified timeline via the shared BuildTicketTimeline (also used by the Export
   * Ticket PDF action): each linked ChannelMessage contributes the inbound customer message
   * and (if present) the agent/AI reply; each IssueComment contributes a staff note (internal)
   * or customer-facing comment. All interleaved chronologically across channels.
   */
  private buildTimeline(
    messages: ChannelMessageEntityExtendedClient[],
    comments: mjBizAppsIssuesIssueCommentEntity[],
    messageNotes: IzzyChannelMessageNoteEntity[],
  ): TimelineEntry[] {
    return BuildTicketTimeline(
      messages,
      comments,
      messageNotes,
      (channelId) => {
        const channel = IzzyMetadataEngine.Instance.Channels.find(c => UUIDsEqual(c.ID, channelId));
        return {
          name: channel?.Name || 'Unknown channel',
          type: channel ? IzzyMetadataEngine.Instance.GetChannelType(channel.ChannelTypeID)?.Name || '' : '',
        };
      },
      (c) => this.commentAuthorNames.get(c.ID),
    );
  }

  /** Resolve display names for comment AND message-note authors (by AuthorPersonID), cached by row ID. */
  private async resolveCommentAuthors(comments: ReadonlyArray<TimelineAuthoredLike>): Promise<void> {
    this.commentAuthorNames.clear();
    const personIds = Array.from(new Set(comments.map(c => c.AuthorPersonID).filter((id): id is string => !!id)));
    if (personIds.length === 0) return;
    const esc = (s: string) => s.replace(/'/g, "''");
    const rv = new RunView();
    const res = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${personIds.map(id => `'${esc(id)}'`).join(', ')})`,
      ResultType: 'entity_object',
    });
    const byPerson = new Map<string, string>();
    for (const p of (res.Success ? res.Results : [])) {
      byPerson.set(p.ID, p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Staff');
    }
    for (const c of comments) {
      if (c.AuthorPersonID && byPerson.has(c.AuthorPersonID)) {
        this.commentAuthorNames.set(c.ID, byPerson.get(c.AuthorPersonID)!);
      }
    }
  }

  /**
   * Composer submit. Two paths by `commentSource`:
   *
   * - **internal note** — persist an internal IssueComment directly (parent-owns-the-write, mirroring
   *   MJ's conversation-chat-area). Staff-only; never sent on the channel.
   * - **outbound reply** — actually SEND it to the customer on the ticket's channel via the
   *   server-side `Send Ticket Reply` action (the browser can't run a communication provider). The
   *   action sends and records the outbound ChannelMessage; we do NOT write an IssueComment here, so a
   *   reply only appears on the timeline if it genuinely went out (no "recorded but not sent" lie).
   */
  /**
   * Initializes the outbound composer's recipient controls from the ticket's latest inbound
   * email (the same message Send Ticket Reply will thread onto): computes the reply-all
   * prefill and opens in the mode the channel's ReplyAllDefault setting dictates. Display
   * prefill only — the server applies full suppression and the recipient cap at send time.
   */
  private initComposerRecipients(messages: ChannelMessageEntityExtendedClient[]): void {
    const inbound = messages
      .filter(m => m.MessageContent && m.ExternalSystemRecordID)
      .sort((a, b) => new Date(b.ReceivedAt).getTime() - new Date(a.ReceivedAt).getTime())[0];
    if (!inbound) {
      this.replyAllAvailable = false;
      this.replyAllPrefill = [];
      this.replyAll = false;
      this.ccInput = '';
      this.composerChannelId = null;
      this.composerThreadContext = null;
      return;
    }
    this.composerChannelId = inbound.ChannelID;
    this.composerThreadContext = inbound.MessageContent;
    const channel = IzzyMetadataEngine.Instance.GetChannel(inbound.ChannelID);
    this.replyAllPrefill = inbound.GetReplyAllPrefill(channel?.ExternalIdentifier);
    this.replyAllAvailable = this.replyAllPrefill.length > 0;
    this.replyAll = this.replyAllAvailable &&
      IzzyMetadataEngine.Instance.GetSettingValueBoolean('ReplyAllDefault', inbound.ChannelID, false);
    this.ccInput = this.replyAll ? this.replyAllPrefill.join(', ') : '';
  }

  /** The composer's Cc as a cleaned address list — shared by the typed-reply and approve-draft paths. */
  private composerCcList(): string[] {
    return this.ccInput.split(',').map(a => a.trim()).filter(a => a.length > 0);
  }

  /** Outlook-style toggle: Reply All fills Cc with the prefill; Reply clears it. */
  protected setComposerReplyMode(replyAll: boolean): void {
    this.replyAll = replyAll;
    this.ccInput = replyAll ? this.replyAllPrefill.join(', ') : '';
  }

  /** Switch composer tabs; the reply editor is recreated on return, seeded with the draft so far. */
  protected setCommentSource(source: 'internal' | 'outbound'): void {
    if (source === this.commentSource) return;
    if (source === 'outbound') {
      this.replyBodySeed = this.replyBody;
    }
    this.commentSource = source;
  }

  /** Whether the active composer (reply editor or internal-note box) has anything to submit. */
  protected composerHasBody(): boolean {
    return this.commentSource === 'outbound' ? !!this.replyBody.trim() : !!this.commentBody.trim();
  }

  async addComment(): Promise<void> {
    // Outbound replies are editor HTML: strip the &nbsp; Quill puts between words before sending.
    const body = (this.commentSource === 'outbound' ? MessageEditorComponent.CleanNbspForSend(this.replyBody) : this.commentBody).trim();
    if (!this.issue || !body || this.isAddingComment) return;
    this.isAddingComment = true;
    this.commentError = '';
    try {
      if (this.commentSource === 'outbound') {
        const result = await this.runAction('Send Ticket Reply', [
          { Name: 'IssueID', Value: this.issue.ID, Type: 'Input' },
          { Name: 'Body', Value: body, Type: 'Input' },
          // Recipient intent: the server action re-applies suppression and the recipient cap.
          { Name: 'CCRecipients', Value: this.ccInput, Type: 'Input' },
          { Name: 'ReplyAll', Value: this.replyAll ? 'true' : 'false', Type: 'Input' },
        ]);
        if (!result.ok) {
          this.commentError = result.message || 'Failed to send reply.';
          return;
        }
        this.replyBody = '';
        this.replyBodySeed = '';
        this.replyEditor?.clear();
        await this.load(); // refresh timeline with the sent outbound message
        return;
      }

      // Internal note — record directly.
      const md = new Metadata();
      const comment = await md.GetEntityObject<mjBizAppsIssuesIssueCommentEntity>('MJ_BizApps_Issues: Issue Comments');
      comment.NewRecord();
      comment.IssueID = this.issue.ID;
      comment.Body = body;
      comment.Source = this.commentSource;
      const authorPersonId = await this.actingPersonId();
      if (authorPersonId) comment.AuthorPersonID = authorPersonId;
      comment.AuthorEmail = md.CurrentUser?.Email ?? null;
      if (!await comment.Save()) {
        this.commentError = 'Failed to add note. Please try again.';
        return;
      }
      this.commentBody = '';
      await this.load(); // refresh timeline with the new note
    } catch (error) {
      console.error('TicketDetail: composer submit failed', error);
      this.commentError = 'Failed to submit. See console for details.';
    } finally {
      this.isAddingComment = false;
    }
  }

  /**
   * The acting staff member's bizapps-common PersonID, or null. Resolved from the current user's
   * own People link (BCSaaS 1.8+ binds users through 'BC: People' and leaves the deprecated
   * People.LinkedUserID empty), with LinkedUserID as the fallback.
   */
  private async actingPersonId(): Promise<string | null> {
    const user = new Metadata().CurrentUser;
    if (!user?.ID) return null;
    try {
      return await ResolvePersonIDForUser(user, Metadata.Provider, user);
    } catch (error) {
      console.error('TicketDetail: acting person lookup failed', error);
      return null;
    }
  }

  // ========== Display helpers ==========

  /** Maps a reply's ApprovalStatus to a badge color class (mirrors the message-card approval badge). */
  getReplyBadgeClass(approvalStatus: string | null | undefined): string {
    switch (approvalStatus) {
      case 'Approved': return 'reply-approved';
      case 'Pending': return 'reply-pending';
      case 'Rejected': return 'reply-rejected';
      case 'Escalated': return 'reply-escalated';
      default: return '';
    }
  }

  /** A reply can be approved/rejected from the ticket view when it's still awaiting a decision. */
  canApproveReply(entry: TimelineEntry): boolean {
    return entry.kind === 'reply'
      && !!entry.messageId
      && (entry.approvalStatus === 'Pending' || entry.approvalStatus === 'Escalated');
  }

  /**
   * Approve the AI/agent reply for a message directly from the ticket timeline. Uses the SAME
   * path as the Review & Approve screen — ChannelMessage.Approve() then Save() — so the server
   * Save() transition sends the reply and the change reflects everywhere (one ChannelMessage record).
   */
  async approveReply(messageId: string | undefined): Promise<void> {
    await this.decideReply(messageId, 'approve');
  }

  /** Reject the AI/agent reply (same ChannelMessage.Reject() path as Review & Approve). */
  async rejectReply(messageId: string | undefined): Promise<void> {
    await this.decideReply(messageId, 'reject');
  }

  /**
   * Enter inline edit of a pending reply using the shared Quill editor (izzy-message-editor).
   * Seeds currentContent with the approved-or-generated reply and originalContent with the
   * entity's EditBaselineContent (the editor's Revert / track-changes baseline) — mirrors
   * message-approval.enterEditMode.
   */
  startEditReply(entry: TimelineEntry): void {
    if (!entry.messageId) { return; }
    const msg = this.messageEntities.find(m => UUIDsEqual(m.ID, entry.messageId!));
    if (!msg) { return; }
    this.editingMessageId = entry.messageId;
    this.editingChannelId = msg.ChannelID;
    this.editingThreadContext = msg.MessageContent;
    this.editingOriginalContent = msg.EditBaselineContent;
    this.editedReplyContent = msg.ApprovedReplyContent || msg.GeneratedReplyContent || '';
  }

  /** Discard the inline edit without saving. */
  cancelEditReply(): void {
    this.editingMessageId = null;
    this.editedReplyContent = '';
    this.editingOriginalContent = '';
  }

  /**
   * Handle the shared editor's save. Mirrors message-approval.onEditorSave: when approved, call
   * ChannelMessage.ApproveWithEdits(content, isManualWrite=false) then Save() (the server Save()
   * override sends). isManualWrite is false — we're editing the AI's generated draft, not writing
   * from scratch (matching enterEditMode's isWriteMode = false).
   */
  async onTicketEditorSave(messageId: string | undefined, event: { content: string; approved: boolean }): Promise<void> {
    if (!messageId || this.approvingMessageId) { return; }
    if (!event.approved) { this.cancelEditReply(); return; }
    const msg = this.messageEntities.find(m => UUIDsEqual(m.ID, messageId));
    if (!msg) { return; }
    this.approvingMessageId = messageId;
    this.commentError = '';
    try {
      msg.ApproveWithEdits(event.content, false);
      await msg.Save();
      this.editingMessageId = null;
      this.editedReplyContent = '';
      this.editingOriginalContent = '';
      await this.load();
    } catch (e) {
      console.error('TicketDetail: onTicketEditorSave failed', e);
      this.commentError = `Failed to approve edited reply: ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      this.approvingMessageId = null;
    }
  }

  /**
   * Save the edited reply as a DRAFT without approving/sending — mirrors
   * message-approval.onEditorSaveDraft: ChannelMessage.SaveDraftContent(content, isManualWrite=false)
   * then Save(). The reply stays Pending with the edited content persisted (no send).
   */
  async onTicketEditorSaveDraft(messageId: string | undefined, content: string): Promise<void> {
    if (!messageId || this.approvingMessageId) { return; }
    const msg = this.messageEntities.find(m => UUIDsEqual(m.ID, messageId));
    if (!msg) { return; }
    this.approvingMessageId = messageId;
    this.commentError = '';
    try {
      msg.SaveDraftContent(content, false);
      await msg.Save();
      this.editingMessageId = null;
      this.editedReplyContent = '';
      this.editingOriginalContent = '';
      await this.load();
    } catch (e) {
      console.error('TicketDetail: onTicketEditorSaveDraft failed', e);
      this.commentError = `Failed to save draft: ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      this.approvingMessageId = null;
    }
  }

  private async decideReply(messageId: string | undefined, action: 'approve' | 'reject'): Promise<void> {
    if (!messageId || this.approvingMessageId) { return; }
    const msg = this.messageEntities.find(m => UUIDsEqual(m.ID, messageId));
    if (!msg) { return; }
    this.approvingMessageId = messageId;
    this.commentError = '';
    try {
      // Mirror the Review & Approve screen exactly (message-approval saveAndAdvance):
      // set the transition on the entity, then await Save() — the server Save() override
      // performs the send. Rely on try/catch for failures (same as the proven path).
      if (action === 'approve') {
        msg.Approve();
        // The composer's Cc / Reply-All choice applies to THIS send too, not just typed replies.
        // Without this the server sees no CC intent and sends to the sender only, silently
        // dropping whatever the agent put in Cc. Suppression and the recipient cap still apply.
        msg.SetPlannedCCRecipients(this.composerCcList());
      } else {
        msg.Reject();
      }
      await msg.Save();
      // Reload so the timeline reflects the new approval state (and the sent reply).
      await this.load();
    } catch (e) {
      console.error(`TicketDetail: ${action}Reply failed`, e);
      this.commentError = `Failed to ${action} reply: ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      this.approvingMessageId = null;
    }
  }

  getChannelIcon(channelType: string): string {
    switch (channelType) {
      case 'Email': return 'fa-solid fa-envelope';
      case 'SMS': return 'fa-solid fa-comment-sms';
      case 'Phone': return 'fa-solid fa-phone';
      default: return 'fa-solid fa-message';
    }
  }

  /** Rail icon per timeline entry kind: notes/comments get their own glyph; messages use the channel icon. */
  getEntryIcon(e: TimelineEntry): string {
    if (e.kind === 'note') return 'fa-solid fa-note-sticky';
    if (e.kind === 'comment') return 'fa-solid fa-comment-dots';
    return this.getChannelIcon(e.channelType);
  }

  getStatusColor(): string {
    return this.status?.ColorCode || 'var(--mj-text-muted)';
  }

  getPriorityClass(): string {
    return `priority-${(this.issue?.Priority || 'medium').toLowerCase()}`;
  }

  /**
   * Transitions the ticket to a new status by setting StatusID and saving the entity. The save
   * goes through MJAPI, where the server-side IssueEntityServer.Save() owns the lifecycle work
   * (timestamp stamping, OnStatusChange hooks) — the browser can't run the server-only action
   * engine, so the UI just mutates + saves and lets the server subclass do the rest. Reloads on
   * success so the badge, SLA, and timeline reflect the change.
   */
  async changeStatus(newStatusId: string): Promise<void> {
    if (!this.issue || !newStatusId || UUIDsEqual(newStatusId, this.issue.StatusID)) {
      return;
    }
    if (!await this.confirmNotStale()) return;
    this.isChangingStatus = true;
    try {
      this.issue.StatusID = newStatusId;
      if (!await this.issue.Save()) {
        this.errorMessage = 'Failed to change status.';
        return;
      }
      await this.load(); // refresh status/SLA/timeline
    } catch (error) {
      console.error('TicketDetail: status change failed', error);
      this.errorMessage = 'Failed to change status.';
    } finally {
      this.isChangingStatus = false;
    }
  }

  // --- Merge (§7.4) -------------------------------------------------------

  /** Open the merge panel and reset its state. */
  openMergePanel(): void {
    this.showMergePanel = true;
    this.mergeSearch = '';
    this.mergeCandidates = [];
    this.selectedSurvivor = null;
    this.mergeError = '';
    this.showReporterMergePrompt = false;
    this.reporterMerge = null;
  }

  closeMergePanel(): void {
    this.showMergePanel = false;
  }

  /**
   * Search OPEN tickets (excluding this one) by ticket number or title to pick the survivor —
   * the ticket THIS one will be merged into.
   */
  async searchMergeCandidates(): Promise<void> {
    const term = this.mergeSearch.trim();
    if (!this.issue || !term) {
      this.mergeCandidates = [];
      return;
    }
    const esc = (s: string) => s.replace(/'/g, "''");
    const rv = new RunView();

    // Org-isolation (PRD §7.4): only tickets in THIS ticket's org are mergeable. The bizapps Issue
    // has no OrganizationID — org lives on the per-org izzy.IssueTicketNumber row — so resolve this
    // ticket's org, then build the candidate set from the IssueIDs that share it. This also gives us
    // each candidate's real per-org TicketNumber ({PREFIX}-####) to display instead of the raw ISS-#.
    const myOrgRes = await rv.RunView<{ OrganizationID: string }>({
      EntityName: 'Issue Ticket Numbers',
      ExtraFilter: `IssueID = '${esc(this.issue.ID)}'`,
      Fields: ['OrganizationID'],
      MaxRows: 1,
    });
    const myOrgId = (myOrgRes.Success ? myOrgRes.Results?.[0]?.OrganizationID : null) ?? null;
    if (!myOrgId) {
      // Legacy ticket with no per-org number — cannot safely scope by org, so show nothing rather
      // than risk surfacing cross-org candidates.
      this.mergeCandidates = [];
      this.mergeError = 'This ticket has no organization-scoped number yet, so it cannot be merged here.';
      return;
    }
    // All ticket numbers in this org, keyed by IssueID — the candidate universe + their display labels.
    const orgTnRes = await rv.RunView<{ IssueID: string; TicketNumber: string }>({
      EntityName: 'Issue Ticket Numbers',
      ExtraFilter: `OrganizationID = '${esc(myOrgId)}'`,
      Fields: ['IssueID', 'TicketNumber'],
    });
    const ticketNumByIssueId = new Map<string, string>(
      (orgTnRes.Success ? orgTnRes.Results ?? [] : []).map(r => [r.IssueID, r.TicketNumber]),
    );
    const orgIssueIds = [...ticketNumByIssueId.keys()].filter(id => !this.issue || id !== this.issue.ID);
    if (orgIssueIds.length === 0) {
      this.mergeCandidates = [];
      return;
    }

    // Exclude terminal-status tickets; match on per-org TicketNumber or title.
    const terminalRes = await rv.RunView<{ ID: string }>({
      EntityName: 'MJ_BizApps_Issues: Issue Status',
      ExtraFilter: 'IsTerminal = 1',
      Fields: ['ID'],
    });
    const terminalIds = (terminalRes.Success ? terminalRes.Results : []).map(s => s.ID);
    // Restrict to this org's IssueIDs whose TicketNumber or Title matches the term.
    const lcTerm = term.toLowerCase();
    const matchingIssueIds = orgIssueIds.filter(id =>
      (ticketNumByIssueId.get(id) || '').toLowerCase().includes(lcTerm),
    );
    let filter = `ID IN (${orgIssueIds.map(id => `'${esc(id)}'`).join(', ')}) ` +
      `AND (Title LIKE '%${esc(term)}%'` +
      (matchingIssueIds.length > 0
        ? ` OR ID IN (${matchingIssueIds.map(id => `'${esc(id)}'`).join(', ')}))`
        : ')');
    if (terminalIds.length > 0) {
      filter += ` AND StatusID NOT IN (${terminalIds.map(id => `'${esc(id)}'`).join(', ')})`;
    }
    const res = await rv.RunView<mjBizAppsIssuesIssueEntity>({
      EntityName: 'MJ_BizApps_Issues: Issues',
      ExtraFilter: filter,
      OrderBy: '__mj_CreatedAt DESC',
      MaxRows: 10,
      ResultType: 'entity_object',
    });
    this.mergeCandidates = (res.Success ? res.Results ?? [] : []).map(i => ({
      id: i.ID,
      // Display the real per-org ticket number ({PREFIX}-####), never the raw bizapps IssueNumber (ISS-#).
      issueNumber: ticketNumByIssueId.get(i.ID) || this.formatTicketLabel(i.IssueNumber),
      title: i.Title,
      reporterPersonId: i.ReporterPersonID ?? null,
    }));
  }

  selectSurvivor(c: MergeCandidate): void {
    this.selectedSurvivor = c;
  }

  /**
   * Merge THIS ticket (loser) INTO the selected survivor by running the server "Merge Tickets"
   * action (the tombstone merge service is server-only — re-points the loser's messages/comments
   * onto the survivor, tombstones the loser, writes a reversible TicketMerge audit row). Afterwards,
   * if the two tickets had different reporters, prompt to also merge the People (§7.3).
   */
  async confirmMerge(): Promise<void> {
    if (!this.issue || !this.selectedSurvivor) {
      return;
    }
    this.isMerging = true;
    this.mergeError = '';
    const loserIssueId = this.issue.ID;
    const loserReporter = this.issue.ReporterPersonID ?? null;
    const survivor = this.selectedSurvivor;
    try {
      const result = await this.runAction('Merge Tickets', [
        { Name: 'SurvivorIssueID', Value: survivor.id, Type: 'Input' },
        { Name: 'LoserIssueID', Value: loserIssueId, Type: 'Input' },
      ]);
      if (!result.ok) {
        // Friendly, client-facing copy. The detailed server message stays in the console only.
        if (result.message) console.warn('Ticket merge failed:', result.message);
        this.mergeError = `Couldn't merge this ticket into ${survivor.issueNumber}. Please try again, or contact support if it keeps happening.`;
        return;
      }

      // Offer to also merge the reporters when they differ (cross-identifier link capture, §7.3).
      if (loserReporter && survivor.reporterPersonId && !UUIDsEqual(loserReporter, survivor.reporterPersonId)) {
        const [survivorName, loserName] = await Promise.all([
          this.personName(survivor.reporterPersonId),
          this.personName(loserReporter),
        ]);
        this.reporterMerge = {
          survivorPersonId: survivor.reporterPersonId,
          loserPersonId: loserReporter,
          survivorName,
          loserName,
        };
        this.showReporterMergePrompt = true;
        this.showMergePanel = false;
        // Stay on the page so the prompt can act; the loser ticket is now tombstoned.
      } else {
        // Same (or unknown) reporter — nothing more to do; leave this (now-merged) ticket.
        this.back.emit();
      }
    } catch (error) {
      console.error('TicketDetail: ticket merge failed', error);
      this.mergeError = 'Merge failed. See console for details.';
    } finally {
      this.isMerging = false;
    }
  }

  /**
   * Merge the two reporters into one Person by running the server "Merge People" action
   * (consolidates ContactMethods — the cross-identifier link capture; tombstones the loser). The
   * staff/member guard (refuse when the loser has an org membership) is enforced server-side.
   */
  async confirmReporterMerge(): Promise<void> {
    if (!this.reporterMerge) {
      return;
    }
    this.isMerging = true;
    this.mergeError = '';
    const { survivorPersonId, loserPersonId } = this.reporterMerge;
    try {
      const result = await this.runAction('Merge People', [
        { Name: 'SurvivorPersonID', Value: survivorPersonId, Type: 'Input' },
        { Name: 'LoserPersonID', Value: loserPersonId, Type: 'Input' },
      ]);
      if (!result.ok) {
        if (result.message) console.warn('Person merge failed:', result.message);
        // The most common, intentional refusal: the person being merged away is a known
        // member/staff contact (protected). Give specific, plain guidance; generic otherwise.
        const raw = (result.message || '').toLowerCase();
        const isProtected = raw.includes('membership') || raw.includes('member/staff');
        this.mergeError = isProtected
          ? `${this.reporterMerge.loserName} is an established contact and can't be merged away. ` +
            `If they're the same person, redo the merge with ${this.reporterMerge.loserName} as the one you keep. The tickets are still merged.`
          : `Couldn't combine these contacts. The tickets are still merged — you can combine the contacts later.`;
        return;
      }
      this.showReporterMergePrompt = false;
      this.back.emit();
    } catch (error) {
      console.error('TicketDetail: person merge failed', error);
      this.mergeError = 'Person merge failed. See console for details.';
    } finally {
      this.isMerging = false;
    }
  }

  /**
   * Run a server MJ Action by name via the GraphQL action runner. Resolves the action ID by name
   * (IDs aren't hardcoded — mj sync assigns them). Returns a simple ok/message result plus any
   * Output params the action set (the server serializes them into ResultData; the client parses
   * them back as an array of {Name, Value}). Pass skipActionLog for actions whose outputs are
   * too large to belong in ActionExecutionLog rows (e.g. a base64 PDF).
   */
  private async runAction(
    actionName: string,
    params: Array<{ Name: string; Value: unknown; Type: 'Input' }>,
    skipActionLog = false,
  ): Promise<{ ok: boolean; message?: string; outputs: Array<{ Name: string; Value: unknown }> }> {
    const provider = Metadata.Provider as GraphQLDataProvider;
    const rv = new RunView();
    const actionRes = await rv.RunView<{ ID: string }>({
      EntityName: 'MJ: Actions',
      ExtraFilter: `Name = '${actionName.replace(/'/g, "''")}'`,
      ResultType: 'simple',
    });
    const actionId = actionRes.Success && actionRes.Results.length > 0 ? actionRes.Results[0].ID : null;
    if (!actionId) {
      return { ok: false, message: `Action "${actionName}" not found in metadata`, outputs: [] };
    }
    const client = new GraphQLActionClient(provider);
    const result = await client.RunAction(actionId, params as never, skipActionLog);
    // ActionResult types Result as the result-code entity, but the GraphQL client actually
    // parses the action's Output params (ResultData JSON) into it — narrow via a guard.
    // MJServer's resolver runs the output array through CopyScalarsAndArrays, which turns an
    // array into an index-keyed OBJECT ({"0": {...}}), so accept both shapes.
    const raw: unknown = result?.Result;
    const candidates: unknown[] = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object' ? Object.values(raw) : [];
    const outputs = candidates.filter((o): o is { Name: string; Value: unknown } =>
      !!o && typeof o === 'object' && typeof (o as { Name?: unknown }).Name === 'string');
    return { ok: !!result?.Success, message: result?.Message, outputs };
  }

  /** Skip the reporter merge — tickets stay merged, People stay separate. */
  skipReporterMerge(): void {
    this.showReporterMergePrompt = false;
    this.back.emit();
  }

  /** Display name for a Person, or the raw ID if not resolvable. */
  private async personName(personId: string): Promise<string> {
    const esc = (s: string) => s.replace(/'/g, "''");
    const rv = new RunView();
    const res = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID='${esc(personId)}'`,
      MaxRows: 1,
      ResultType: 'entity_object',
    });
    const p = res.Success ? res.Results?.[0] : null;
    return p ? `${p.FirstName} ${p.LastName}`.trim() || (p.Email ?? personId) : personId;
  }

  // --- Splitter drag (resize the properties pane) ---

  startSplitterDrag(event: MouseEvent): void {
    event.preventDefault();
    this.isDraggingSplitter = true;
  }

  @HostListener('document:mousemove', ['$event'])
  onSplitterMouseMove(event: MouseEvent): void {
    if (!this.isDraggingSplitter || !this.detailBody) return;
    const rect = this.detailBody.nativeElement.getBoundingClientRect();
    // Properties pane is on the right, so its width = distance from the cursor to the right edge.
    const fromRight = rect.right - event.clientX;
    const pct = (fromRight / rect.width) * 100;
    this.propsWidthPct = Math.max(20, Math.min(60, pct));
  }

  @HostListener('document:mouseup')
  onSplitterMouseUp(): void {
    this.isDraggingSplitter = false;
  }

  onBack(): void {
    this.back.emit();
  }
}

export function LoadTicketDetailComponent(): void {}
